import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import { EmptyState } from '../feedback/EmptyState';

export interface TileOption {
  value: string;
  title: string;
  /** A line or two on what it is. Left out when there's nothing to say. */
  description?: string;
  /** A short fact under the description, e.g. "Created 8 days ago". */
  meta?: string;
  /** A small icon in a tinted square, e.g. a chart glyph for a dashboard. */
  icon?: React.ReactNode;
}

export type TileProps = Omit<TileOption, 'value'> & {
  selected?: boolean;
  onClick?: () => void;
} & Omit<React.ComponentPropsWithoutRef<'button'>, 'title' | 'onClick' | 'color'>;

/**
 * One choice as a card: an icon, a name, a line on what it is, and a fact
 * under that. Selected, it takes the brand outline and a tick.
 *
 * Usually used through `TilePicker`; on its own it is a large button, e.g.
 * for picking what to create. Not for navigation lists — use a list there.
 */
export const Tile = React.forwardRef<HTMLButtonElement, TileProps>(function Tile(
  { title, description, meta, icon, selected = false, onClick, ...rest },
  ref,
) {
  const clamp = (lines: number) => ({
    display: '-webkit-box',
    WebkitLineClamp: lines,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden',
    overflowWrap: 'anywhere' as const,
  });
  return (
    <ButtonBase
      ref={ref}
      onClick={onClick}
      {...rest}
      sx={(t) => ({
        width: '100%',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        gap: 1.5,
        p: 1.5,
        textAlign: 'left',
        borderRadius: `${t.ds.radius.md}px`,
        border: `1px solid ${selected ? t.tokens.brand.primary : t.tokens.border.subtle}`,
        // A second line of outline inside, so selecting doesn't shift the layout.
        boxShadow: selected ? `inset 0 0 0 1px ${t.tokens.brand.primary}` : 'none',
        bgcolor: selected ? t.tokens.brand.primarySofter : t.tokens.surface.raised,
        transition: `border-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}, background-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
        '&:hover': { borderColor: selected ? t.tokens.brand.primary : t.tokens.border.strong },
        '&.Mui-focusVisible': { boxShadow: t.tokens.shadow.focusRing },
      })}
    >
      {icon && (
        <Box
          aria-hidden
          sx={(t) => ({
            width: t.ds.sizes.control.lg,
            height: t.ds.sizes.control.lg,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: `${t.ds.radius.sm}px`,
            bgcolor: t.tokens.brand.primarySoft,
            color: t.tokens.brand.primary,
          })}
        >
          {icon}
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" component="span" sx={clamp(2)}>
          {title}
        </Typography>
        {description && (
          <Typography variant="body2" component="span" sx={(t) => ({ ...clamp(2), color: t.tokens.text.secondary, mt: 0.25 })}>
            {description}
          </Typography>
        )}
        {meta && (
          <Typography variant="caption" component="span" sx={(t) => ({ display: 'block', color: t.tokens.text.tertiary, mt: 0.5 })}>
            {meta}
          </Typography>
        )}
      </Box>
      {selected && (
        <CheckCircleRounded fontSize="small" aria-hidden sx={(t) => ({ flexShrink: 0, color: t.tokens.brand.primary })} />
      )}
    </ButtonBase>
  );
});

export interface TilePickerProps {
  options: TileOption[];
  /** The chosen option's value; `null` for none yet. */
  value: string | null;
  onChange: (value: string) => void;
  /** Names the set for screen readers, e.g. "Dashboards". */
  ariaLabel: string;
  /** Tiles per row on wider screens; always one per row on a phone. */
  columns?: 1 | 2 | 3;
  /** Scroll beyond this height, px. */
  maxHeight?: number;
  /** Shown instead of tiles when there are none. */
  emptyMessage?: string;
}

/**
 * Choose one of a handful of things shown as cards — which dashboard,
 * which template. Better than a dropdown when each choice needs a line of
 * context to tell it apart.
 *
 * Works like a set of radio buttons: Tab reaches the chosen tile (or the
 * first), arrow keys move the choice along. For long lists, filter with a
 * `SearchField` above it; for plain labels, use `SegmentedControl` or
 * `SelectMenu`.
 */
export function TilePicker({
  options,
  value,
  onChange,
  ariaLabel,
  columns = 2,
  maxHeight,
  emptyMessage = 'Nothing to choose from.',
}: TilePickerProps) {
  const refs = React.useRef<Array<HTMLButtonElement | null>>([]);
  if (!options.length) return <EmptyState message={emptyMessage} height={120} />;

  const chosen = options.findIndex((o) => o.value === value);
  const tabStop = chosen >= 0 ? chosen : 0;
  const move = (from: number, step: number) => {
    const next = (from + step + options.length) % options.length;
    refs.current[next]?.focus();
    onChange(options[next].value);
  };

  return (
    <Box
      role="radiogroup"
      aria-label={ariaLabel}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: `repeat(${columns}, minmax(0, 1fr))` },
        gridAutoRows: 'max-content',
        alignContent: 'start',
        gap: 1.5,
        maxHeight,
        overflowY: maxHeight ? 'auto' : undefined,
        // Room for the focus ring at the edges of a scrolling grid.
        p: 0.25,
        m: -0.25,
      }}
    >
      {options.map((o, i) => (
        <Tile
          key={o.value}
          ref={(el) => {
            refs.current[i] = el;
          }}
          role="radio"
          aria-checked={o.value === value}
          tabIndex={i === tabStop ? 0 : -1}
          title={o.title}
          description={o.description}
          meta={o.meta}
          icon={o.icon}
          selected={o.value === value}
          onClick={() => onChange(o.value)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
              e.preventDefault();
              move(i, 1);
            } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
              e.preventDefault();
              move(i, -1);
            }
          }}
        />
      ))}
    </Box>
  );
}
